import { servicePost } from "./client.ts";

/* ── Types ───────────────────────────────────────────────── */

export interface CSPrice {
  ID: number;
  Category: number | null;
  TypeName: string;
  Deleted: boolean;
}

export interface GenericPriceType {
  PriceTypeID: number;
  Category: number;
  TypeName: string;
  Deleted: boolean;
  CalcTS: number;
}

export interface GenericPrice {
  PriceType: number;
  Product: number;
  Category: number | null;
  Type: number | null;
  Kiosk: number | null;
  Location: number | null;
  Chain: number | null;
  District: number | null;
  State: number | null;
  OperatorID: number | null;
  Value: number;
  Weight: number;
  CalcTS: number;
  Deleted: boolean;
}

export interface GenericPriceData {
  Settings: GenericPrice[];
  Types: GenericPriceType[];
}

/* ── API functions ───────────────────────────────────────── */

export async function getPricesCS(): Promise<CSPrice[]> {
  const result = await servicePost<CSPrice[]>("WebGetPricesCS", {});
  if (result.bError) throw new Error(result.sMessage ?? "Failed to load prices");
  return result.oData ?? [];
}

export async function addPriceType(price: CSPrice): Promise<boolean> {
  const result = await servicePost<boolean>("WebAddPriceType", { price });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to add price type");
  return result.oData ?? false;
}

export async function deletePriceType(id: number): Promise<boolean> {
  const result = await servicePost<boolean>("WebDeletePriceType", { id });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to delete price type");
  return result.oData ?? false;
}

export async function getAdvancedPriceData(): Promise<GenericPriceData> {
  const result = await servicePost<GenericPriceData>("GetAdvancedPriceData", {
    settingTS: 0,
    typeTS: 0,
  });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to load price data");
  return result.oData ?? { Settings: [], Types: [] };
}

export async function saveAdvancedPriceData(items: GenericPrice[]): Promise<boolean> {
  const result = await servicePost<boolean>("SaveAdvancedPriceData", { items });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to save price data");
  return result.oData ?? false;
}

/* ── "From file" resolve (preview, server-side, no write) ─────────────────── */

/** One resolved override scope + its prices (PriceType id → value). Keys are strings
 *  because the server's Dictionary<int,decimal> serializes with string keys. */
export interface PriceResolveRow {
  Product: number;
  Type: number;
  Category: number;
  Kiosk: number;
  Location: number;
  Chain: number;
  District: number;
  State: number;
  OperatorID: number;
  Prices: Record<string, number>;
}

/** One unresolved input row (dropped by the deref) + why — for debugging "From file". */
export interface PriceResolveUnresolvedRow {
  Product: number;
  ProductRef: string | null;
  Type: number;
  Category: number;
  Chain: number;
  District: number;
  State: number;
  Kiosk: number;
  KioskRef: string | null;
  Location: number;
  LocationRef: string | null;
  OperatorID: number;
  OperatorRef: string | null;
  Reason: string | null;
}

export interface PriceResolveResult {
  Rows: PriceResolveRow[];
  Total: number;
  /** Rows skipped because a product/kiosk/operator ref couldn't be matched. */
  Unresolved: number;
  /** Rows dropped because they fell outside the caller's ingress scope. */
  Dropped: number;
  /** Price points skipped because they already match the current override. */
  Unchanged: number;
  Messages: string[];
  /** Detail for the unresolved rows (capped at 500) — raw ids/refs + reason, for debugging. */
  UnresolvedRows?: PriceResolveUnresolvedRow[];
}

/**
 * Resolve an already-uploaded price file (same layout as the GenericPrice import)
 * into stage-ready override rows. Server parses + resolves refs from cache + drops
 * rows the caller lacks permission for. Nothing is written — the client stages the
 * rows into "Changes Made" for review + Save.
 */
export async function resolveGenericPriceFile(storedPath: string): Promise<PriceResolveResult> {
  const result = await servicePost<PriceResolveResult>("WebResolveGenericPriceFile", { storedPath });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to resolve file");
  return result.oData ?? { Rows: [], Total: 0, Unresolved: 0, Dropped: 0, Unchanged: 0, Messages: [] };
}

/* ── Per-kiosk inventory price overview ──────────────────── */

export interface KioskInventoryPriceItem {
  ProductID: number;
  /** Resolved price values keyed by PriceType (1=Price, 2=Tax, 5=EH Price, 6=EH Tax).
   *  Backend serializes Dictionary<int, decimal> as a JSON object with string keys. */
  Prices: Record<string, number>;
}

export interface KioskPriceOverview {
  KioskID: number;
  LocationID: number;
  OperatorID: number;
  /** Resolved scope IDs (S_Admin_CB_Values.sFilterValue), matching the kiosk
   *  resolver S_Kiosk_Product_Price_FN: Chain←sChain, District←sDistrict, State←sState.
   *  0 when unset. */
  Chain: number;
  District: number;
  State: number;
  Products: KioskInventoryPriceItem[];
}

export async function getKioskPriceOverview(kioskID: number): Promise<KioskPriceOverview> {
  const result = await servicePost<KioskPriceOverview>("WebGetKioskPriceOverview", { kioskID });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to load kiosk prices");
  return result.oData ?? { KioskID: kioskID, LocationID: 0, OperatorID: 0, Chain: 0, District: 0, State: 0, Products: [] };
}

/* ── Per-location price overview (EditLocation → Prices tab) ── */

export interface LocationKioskProductPrice {
  KioskID: number;
  ProductID: number;
  /** Authoritative resolved prices keyed by PriceType (string keys on the wire). */
  Prices: Record<string, number>;
}

export interface LocationPriceOverview {
  LocationID: number;
  OperatorID: number;
  /** Resolved scope IDs (S_Admin_CB_Values.sFilterValue): Chain←sChain,
   *  District←sDistrict (from L.sCounty), State←sState (from L.sDistrict). 0 when unset. */
  Chain: number;
  District: number;
  State: number;
  KioskIDs: number[];
  Products: number[];
  KioskPrices: LocationKioskProductPrice[];
}

export async function getLocationPriceOverview(locationID: number): Promise<LocationPriceOverview> {
  const result = await servicePost<LocationPriceOverview>("WebGetLocationPriceOverview", { locationID });
  if (result.bError) throw new Error(result.sMessage ?? "Failed to load location prices");
  return result.oData ?? { LocationID: locationID, OperatorID: 0, Chain: 0, District: 0, State: 0, KioskIDs: [], Products: [], KioskPrices: [] };
}

/* ── Location → per-kiosk price divergence drill-down ────── */

/** One kiosk under a location whose resolved Price differs from the
 *  location-effective Price for a given product (Location Prices tab
 *  DiffCount drill-down). */
export interface KioskDivergenceRow {
  KioskID: number;
  /** The kiosk's override values keyed by PriceType (string keys on the wire),
   *  e.g. {"3": 1.0, "4": 0.0} — exactly what this kiosk overrides. */
  OverrideValues: Record<string, number>;
}

export async function getLocationProductKioskDivergence(
  locationID: number,
  productID: number,
): Promise<KioskDivergenceRow[]> {
  const result = await servicePost<KioskDivergenceRow[]>(
    "WebGetLocationProductKioskDivergence",
    { locationID, productID },
  );
  if (result.bError) throw new Error(result.sMessage ?? "Failed to load divergent kiosks");
  return result.oData ?? [];
}
